import React, { useState } from 'react';

// EMIT Sermon: builds a sermon, Bible study, or devotional, then hands the
// request to the chat so the user can revise it in conversation.
const TYPES = {
  sermon: { label: 'Sermon', lengths: [10, 20, 30, 45], def: 20 },
  study: { label: 'Bible Study', lengths: [20, 30, 45], def: 30 },
  devotional: { label: 'Devotional', lengths: [3, 5, 10], def: 5 },
};
const SETTINGS = ['Church service', 'Team or corporate', 'Airport chapel', 'Hospital', 'Small group'];

const GOLD = '#fbbf24';
const chip = (on) => ({
  minHeight: '44px', padding: '0 16px', borderRadius: '999px', cursor: 'pointer',
  fontSize: '15px', fontWeight: 600, fontFamily: 'inherit',
  border: on ? '2px solid ' + GOLD : '1px solid rgba(255,255,255,0.28)',
  background: on ? 'rgba(251,191,36,0.18)' : 'transparent',
  color: on ? '#fde68a' : '#e2e8f0',
});
const label = { display: 'block', color: '#cbd5e1', fontSize: '15px', fontWeight: 700, margin: '18px 0 8px' };
const field = {
  width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: '10px', fontSize: '16px',
  fontFamily: 'inherit', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.25)', color: '#ffffff',
};

const SermonBuilder = ({ onBuild, disabled }) => {
  const [type, setType] = useState('sermon');
  const [format, setFormat] = useState('Outline');
  const [setting, setSetting] = useState('Church service');
  const [length, setLength] = useState(TYPES.sermon.def);
  const [passage, setPassage] = useState('');
  const [notes, setNotes] = useState('');

  const pickType = (t) => { setType(t); setLength(TYPES[t].def); };
  const ready = passage.trim().length > 0 && !disabled;

  const build = () => {
    if (!ready) return;
    const lines = [
      'Build a ' + TYPES[type].label.toLowerCase() + '.',
      type === 'sermon' ? 'Format: ' + (format === 'Outline' ? 'outline' : 'full manuscript') : null,
      'Setting: ' + setting,
      'Length: about ' + length + ' minutes',
      'Passage or theme: ' + passage.trim(),
      notes.trim() ? 'Audience and occasion: ' + notes.trim() : null,
    ].filter(Boolean);
    onBuild(lines.join('\n'));
  };

  return (
    <div style={{ padding: '16px', display: 'flex', justifyContent: 'center' }}>
      <div style={{
        width: '100%', maxWidth: '640px', boxSizing: 'border-box', padding: '22px 20px',
        background: 'linear-gradient(135deg,#0f172a 0%,#1e1b4b 100%)',
        border: '1px solid rgba(251,191,36,0.35)', borderRadius: '16px', color: '#ffffff',
      }}>
        <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#fde68a' }}>Build a message</h2>
        <p style={{ margin: '6px 0 0', color: '#cbd5e1', fontSize: '15px', lineHeight: 1.5 }}>
          A sermon, Bible study, or devotional, with a closing prayer and a memory verse. Ask for changes after it is built.
        </p>

        <span style={label}>What are you building?</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {Object.entries(TYPES).map(([k, t]) => (
            <button key={k} type="button" aria-pressed={type === k} style={chip(type === k)} onClick={() => pickType(k)}>{t.label}</button>
          ))}
        </div>

        {type === 'sermon' && (<>
          <span style={label}>Format</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {['Outline', 'Full manuscript'].map(f => (
              <button key={f} type="button" aria-pressed={format === f} style={chip(format === f)} onClick={() => setFormat(f)}>{f}</button>
            ))}
          </div>
        </>)}

        <span style={label}>Setting</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {SETTINGS.map(s => (
            <button key={s} type="button" aria-pressed={setting === s} style={chip(setting === s)} onClick={() => setSetting(s)}>{s}</button>
          ))}
        </div>

        <span style={label}>Length</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {TYPES[type].lengths.map(n => (
            <button key={n} type="button" aria-pressed={length === n} style={chip(length === n)} onClick={() => setLength(n)}>{n} min</button>
          ))}
        </div>

        <label style={label} htmlFor="sb-passage">Passage or theme</label>
        <input id="sb-passage" style={field} value={passage} onChange={e => setPassage(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') build(); }}
          placeholder="Psalm 23, or peace in the middle of a hard week" />

        <label style={label} htmlFor="sb-notes">Audience and occasion <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></label>
        <textarea id="sb-notes" style={{ ...field, minHeight: '84px', resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Who will be there, and what is going on for them" />

        <button type="button" onClick={build} disabled={!ready} style={{
          marginTop: '20px', width: '100%', minHeight: '52px', borderRadius: '12px', border: 'none',
          fontSize: '17px', fontWeight: 800, fontFamily: 'inherit', cursor: ready ? 'pointer' : 'not-allowed',
          background: ready ? GOLD : 'rgba(251,191,36,0.3)', color: '#1e1b4b',
        }}>{disabled ? 'Working\u2026' : 'Build it'}</button>
      </div>
    </div>
  );
};

export default SermonBuilder;
